/**
 * Step 1: Business Information
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MapPin, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  { value: 'grocery_store', label: 'Grocery Store' },
  { value: 'supermarket', label: 'Supermarket' },
  { value: 'convenience_store', label: 'Convenience Store' },
  { value: 'specialty_food', label: 'Specialty Food Store' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'butcher', label: 'Butcher Shop' },
  { value: 'produce_market', label: 'Produce Market' },
  { value: 'organic_natural', label: 'Organic/Natural Foods' },
  { value: 'ethnic_grocery', label: 'Ethnic Grocery' },
  { value: 'other', label: 'Other' }
];

export default function BusinessInfoStep({ data, onComplete, onBack }) {
  const [formData, setFormData] = useState({
    store_name: data?.store_name || '',
    business_type: data?.business_type || '',
    store_address: data?.store_address || '',
    store_address_line2: data?.store_address_line2 || '',
    store_city: data?.store_city || '',
    store_state: data?.store_state || '',
    store_zip: data?.store_zip || '',
    store_country: data?.store_country || 'US',
    store_phone: data?.store_phone || ''
  });
  const [saving, setSaving] = useState(false);
  const [addressValidated, setAddressValidated] = useState(!!data?.latitude);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Reset address validation if address changes
    if (['store_address', 'store_city', 'store_state', 'store_zip'].includes(field)) {
      setAddressValidated(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Auto-format to E.164
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    return value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Format phone before sending
      const submitData = {
        ...formData,
        store_phone: formatPhoneNumber(formData.store_phone)
      };

      const response = await fetch(`${API_URL}/api/retailer/onboarding/business-info`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (response.ok) {
        setAddressValidated(true);
        toast.success('Business information saved!');
        onComplete(result);
      } else {
        const errors = result.detail?.errors || [result.detail];
        errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error('Failed to save business information');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Store Name */}
        <div className="md:col-span-2">
          <Label htmlFor="store_name">Store Name *</Label>
          <Input
            id="store_name"
            value={formData.store_name}
            onChange={(e) => handleChange('store_name', e.target.value)}
            placeholder="Your Store Name"
            required
            data-testid="store-name-input"
          />
        </div>

        {/* Business Type */}
        <div className="md:col-span-2">
          <Label>Business Type *</Label>
          <Select
            value={formData.business_type}
            onValueChange={(value) => handleChange('business_type', value)}
            required
          >
            <SelectTrigger data-testid="business-type-select">
              <SelectValue placeholder="Select business type" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Address */}
        <div className="md:col-span-2">
          <Label htmlFor="store_address">Street Address *</Label>
          <div className="relative">
            <Input
              id="store_address"
              value={formData.store_address}
              onChange={(e) => handleChange('store_address', e.target.value)}
              placeholder="123 Main Street"
              required
              data-testid="store-address-input"
            />
            {addressValidated && (
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="store_address_line2">Address Line 2</Label>
          <Input
            id="store_address_line2"
            value={formData.store_address_line2}
            onChange={(e) => handleChange('store_address_line2', e.target.value)}
            placeholder="Suite, Unit, Building (optional)"
          />
        </div>

        {/* City, State, ZIP */}
        <div>
          <Label htmlFor="store_city">City *</Label>
          <Input
            id="store_city"
            value={formData.store_city}
            onChange={(e) => handleChange('store_city', e.target.value)}
            placeholder="City"
            required
            data-testid="store-city-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="store_state">State *</Label>
            <Input
              id="store_state"
              value={formData.store_state}
              onChange={(e) => handleChange('store_state', e.target.value.toUpperCase())}
              placeholder="NY"
              maxLength={2}
              required
              data-testid="store-state-input"
            />
          </div>
          <div>
            <Label htmlFor="store_zip">ZIP *</Label>
            <Input
              id="store_zip"
              value={formData.store_zip}
              onChange={(e) => handleChange('store_zip', e.target.value)}
              placeholder="10001"
              required
              data-testid="store-zip-input"
            />
          </div>
        </div>

        {/* Phone */}
        <div className="md:col-span-2">
          <Label htmlFor="store_phone">Store Phone Number *</Label>
          <Input
            id="store_phone"
            type="tel"
            value={formData.store_phone}
            onChange={(e) => handleChange('store_phone', e.target.value)}
            placeholder="+1 (555) 123-4567"
            required
            data-testid="store-phone-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            This will be verified via SMS
          </p>
        </div>
      </div>

      {/* Address validation note */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <MapPin className="w-4 h-4" />
        <span>Address will be validated using Google Maps</span>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="submit"
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-business-info-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Validating...
            </>
          ) : (
            'Save & Continue'
          )}
        </Button>
      </div>
    </form>
  );
}
